import React, { useEffect } from "react";
import { Link } from "react-router-dom";

// AudRI GxP landing page — ported verbatim from the standalone HTML brief.
// Two tiles: Life Sciences / GxP (primary, → /dashboard) + ICF Management
// (muted "Sister Product" card, → /icf/story). Design system is
// DM Sans + DM Serif Display, ivory / terracotta / navy — matches the
// Insurance app's visual language exactly.

const FONT_HREF =
    "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=DM+Serif+Display&display=swap";

const CSS = `
    :root.audri-landing {
      --bg-gradient: linear-gradient(180deg, #FDFAF7 0%, #FFFFFF 55%);
      --ink-900: #0F1220;
      --ink-700: #2E3448;
      --ink-500: #55607A;
      --ink-300: rgba(15, 18, 32, 0.42);
      --paper-200: rgba(15, 18, 32, 0.06);
      --paper-300: rgba(15, 18, 32, 0.08);
      --accent-600: #CD592A;
      --accent-700: #B94721;
      --accent-50:  #FFF8EE;
      --success-fg: #18895C;
      --success-dk: #0F7A3E;
      --font-sans: "DM Sans", system-ui, -apple-system, "Helvetica Neue", Arial, sans-serif;
      --font-serif: "DM Serif Display", Georgia, "Times New Roman", serif;
    }
    .audri-landing-root, .audri-landing-root * { box-sizing: border-box; }
    .audri-landing-root {
      margin: 0;
      padding: 0;
      background: var(--bg-gradient);
      color: var(--ink-900);
      font-family: var(--font-sans) !important;
      -webkit-font-smoothing: antialiased;
      min-height: 100vh;
    }
    .audri-landing-root h1, .audri-landing-root h2, .audri-landing-root h3,
    .audri-landing-root p, .audri-landing-root span, .audri-landing-root a {
      font-family: var(--font-sans);
    }
    .audri-landing-root .wordmark { font-family: var(--font-serif) !important; }
    .audri-landing-root .page {
      max-width: 1120px;
      margin: 0 auto;
      padding: 40px 24px;
      min-height: 100vh;
      display: flex;
      flex-direction: column;
    }
    .audri-landing-root .brand { display: flex; justify-content: center; margin-bottom: 40px; }
    .audri-landing-root .brand-lockup { display: inline-flex; align-items: center; gap: 12px; }
    .audri-landing-root .brand-mark {
      width: 46px; height: 46px; border-radius: 10px;
      background: var(--accent-600);
      display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0;
    }
    .audri-landing-root .brand-stack { display: flex; flex-direction: column; gap: 2px; }
    .audri-landing-root .wordmark {
      font-family: var(--font-serif); font-size: 30px; line-height: 1;
      color: var(--accent-600); letter-spacing: -0.005em;
    }
    .audri-landing-root .tagline {
      font-size: 10px; letter-spacing: 0.22em; text-transform: uppercase;
      color: var(--ink-500); font-weight: 600;
    }
    .audri-landing-root .hero {
      display: flex; flex-direction: column; align-items: center; gap: 18px;
      text-align: center; max-width: 900px; margin: 0 auto 48px;
    }
    .audri-landing-root .hero-bar { width: 56px; height: 4px; border-radius: 4px; background: var(--accent-600); }
    .audri-landing-root .hero-title {
      font-size: clamp(52px, 7.4vw, 96px); font-weight: 800; line-height: 1.02;
      letter-spacing: -0.035em; margin: 0; color: var(--ink-900);
    }
    .audri-landing-root .hero-sub {
      font-size: clamp(18px, 1.8vw, 22px); line-height: 1.5;
      color: var(--ink-500); margin: 0; max-width: 720px;
    }
    .audri-landing-root .cards {
      display: grid; grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 20px; align-items: stretch;
    }
    .audri-landing-root .card-primary {
      display: flex; flex-direction: column; gap: 20px;
      padding: 40px 40px 36px; border-radius: 20px;
      background: #FFFFFF; border: 1px solid var(--paper-300);
      border-top: 4px solid var(--accent-600);
      box-shadow: 0 8px 24px rgba(15, 18, 32, 0.06);
      color: inherit; min-height: 220px;
      transition: border-color 0.22s ease, box-shadow 0.22s ease, transform 0.22s ease;
      text-decoration: none;
    }
    .audri-landing-root .card-primary:hover {
      border-color: var(--accent-600);
      box-shadow: 0 24px 56px rgba(205, 89, 42, 0.16), 0 4px 12px rgba(15, 18, 32, 0.05);
      transform: translateY(-4px);
    }
    .audri-landing-root .card-header { display: flex; justify-content: space-between; align-items: flex-start; }
    .audri-landing-root .card-eyebrow {
      font-size: 12px; font-weight: 700; letter-spacing: 1.4px;
      text-transform: uppercase; color: var(--accent-600);
    }
    .audri-landing-root .chip-live {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 5px 11px; border-radius: 999px;
      background: rgba(24, 137, 92, 0.10); color: var(--success-dk);
      font-size: 11px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase;
    }
    .audri-landing-root .chip-live::before {
      content: ""; width: 7px; height: 7px; border-radius: 999px;
      background: var(--success-fg); box-shadow: 0 0 0 3px rgba(24, 137, 92, 0.18);
    }
    .audri-landing-root .card-title {
      font-size: clamp(28px, 3.4vw, 40px); font-weight: 700; line-height: 1.08;
      letter-spacing: -0.02em; margin: 0; color: var(--ink-900);
    }
    .audri-landing-root .card-body {
      font-size: 17px; line-height: 1.55; color: var(--ink-500); margin: 0; max-width: 640px;
    }
    .audri-landing-root .features {
      display: flex; flex-wrap: wrap; gap: 20px;
      padding-top: 16px; border-top: 1px solid var(--paper-200); margin-top: 4px;
    }
    .audri-landing-root .feature {
      display: inline-flex; align-items: center; gap: 8px;
      font-size: 14px; font-weight: 500; color: var(--ink-500);
    }
    .audri-landing-root .feature::before {
      content: ""; width: 6px; height: 6px; border-radius: 999px;
      background: var(--accent-600); flex-shrink: 0;
    }
    .audri-landing-root .cta-row {
      display: flex; flex-wrap: wrap; gap: 12px; align-items: center; margin-top: 4px;
    }
    .audri-landing-root .btn-primary {
      display: inline-flex; align-items: center; gap: 10px;
      padding: 14px 22px; border-radius: 999px;
      background: var(--accent-600); color: #fff;
      font-weight: 700; font-size: 14px; letter-spacing: 0.4px; text-decoration: none;
      box-shadow: 0 4px 12px rgba(205, 89, 42, 0.22);
      transition: background 0.2s ease, gap 0.2s ease, box-shadow 0.2s ease;
    }
    .audri-landing-root .card-primary:hover .btn-primary {
      background: var(--accent-700); gap: 14px;
      box-shadow: 0 8px 20px rgba(205, 89, 42, 0.30);
    }
    .audri-landing-root .btn-primary .arrow { transition: transform 0.2s ease; display: inline-block; }
    .audri-landing-root .card-primary:hover .btn-primary .arrow { transform: translateX(4px); }
    .audri-landing-root .btn-secondary {
      display: inline-flex; align-items: center; gap: 8px;
      padding: 13px 20px; border-radius: 999px;
      background: #FFFFFF; border: 1px solid rgba(15, 18, 32, 0.16);
      color: var(--ink-900); font-weight: 700; font-size: 14px; letter-spacing: 0.4px;
      text-decoration: none;
      transition: border-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
    }
    .audri-landing-root .btn-secondary:hover {
      border-color: var(--accent-600); color: var(--accent-600); transform: translateY(-1px);
    }
    .audri-landing-root .btn-secondary svg { display: block; }
    .audri-landing-root .spacer { flex: 1; }
    .audri-landing-root footer {
      text-align: center; font-size: 12px; color: var(--ink-500); padding: 24px 0 8px;
    }
    @media (max-width: 860px) {
      .audri-landing-root .cards { grid-template-columns: 1fr; }
      .audri-landing-root .card-primary { padding: 28px 24px 24px; }
      .audri-landing-root .hero-title { font-size: 44px; }
    }
`;

const DaisyMark = () => (
    <svg width="30" height="30" viewBox="0 0 48 48" aria-label="AudRI">
        <g fill="#FAE0E3">
            {[0, 36, 72, 108, 144, 180, 216, 252, 288, 324].map((r) => (
                <ellipse
                    key={r}
                    cx="24"
                    cy="12"
                    rx="3.2"
                    ry="9.6"
                    transform={`rotate(${r} 24 24)`}
                />
            ))}
        </g>
    </svg>
);

export default function AppChooser() {
    // Inject the font stylesheet + activate the landing-scoped CSS variables
    // only while this page is mounted (prevents them from leaking into the
    // rest of the app, which has its own design tokens).
    useEffect(() => {
        document.documentElement.classList.add("audri-landing");
        return () => {
            document.documentElement.classList.remove("audri-landing");
        };
    }, []);

    return (
        <>
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link
                rel="preconnect"
                href="https://fonts.gstatic.com"
                crossOrigin=""
            />
            <link rel="stylesheet" href={FONT_HREF} />
            <style>{CSS}</style>

            <div className="audri-landing-root">
                <main className="page">
                    {/* Brand header */}
                    <header className="brand">
                        <span className="brand-lockup">
                            <span className="brand-mark">
                                <DaisyMark />
                            </span>
                            <span className="brand-stack">
                                <span className="wordmark">audri</span>
                                <span className="tagline">The Audit Platform</span>
                            </span>
                        </span>
                    </header>

                    {/* Hero */}
                    <section className="hero">
                        <span className="hero-bar" />
                        <h1 className="hero-title">The Audit Platform</h1>
                        <p className="hero-sub">
                            One compliance engine. Every regulated document.
                            Live for life sciences today — built to extend
                            across regulated industries.
                        </p>
                    </section>

                    {/* Cards */}
                    <section className="cards">
                        {/* GxP Validation */}
                        <div
                            className="card-primary"
                            data-testid="landing-card-gxp"
                        >
                            <div className="card-header">
                                <span className="card-eyebrow">
                                    GxP Validation
                                </span>
                                <span className="chip-live">Live Pilot</span>
                            </div>

                            <h2 className="card-title">
                                Every batch, protocol, and record — validated.
                            </h2>

                            <p className="card-body">
                                Extract, execute, and explain compliance rules
                                from validation packages, SOPs, and IQ/OQ/PQ
                                artifacts — with the evidence trail auditors
                                expect.
                            </p>

                            <div className="features">
                                <span className="feature">
                                    100% record coverage
                                </span>
                                <span className="feature">
                                    Rule-attributed deviations
                                </span>
                                <span className="feature">
                                    Auditor-ready evidence
                                </span>
                            </div>

                            <div className="spacer" />

                            <div className="cta-row">
                                <Link
                                    to="/dashboard"
                                    className="btn-primary"
                                    data-testid="landing-gxp-enter"
                                >
                                    Enter the pilot
                                    <span className="arrow">→</span>
                                </Link>
                                <a
                                    href="https://calendly.com/keshav-glifiq/glif-demo"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-secondary"
                                    data-testid="landing-gxp-book"
                                >
                                    <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        aria-hidden="true"
                                    >
                                        <rect
                                            x="3.5"
                                            y="5"
                                            width="17"
                                            height="15"
                                            rx="2.5"
                                            stroke="currentColor"
                                            strokeWidth="1.8"
                                        />
                                        <path
                                            d="M3.5 9.5H20.5"
                                            stroke="currentColor"
                                            strokeWidth="1.8"
                                        />
                                        <path
                                            d="M8 3V7"
                                            stroke="currentColor"
                                            strokeWidth="1.8"
                                            strokeLinecap="round"
                                        />
                                        <path
                                            d="M16 3V7"
                                            stroke="currentColor"
                                            strokeWidth="1.8"
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                    Book a meeting
                                </a>
                            </div>
                        </div>

                        {/* ICF Management */}
                        <div
                            className="card-primary"
                            data-testid="landing-card-icf"
                        >
                            <div className="card-header">
                                <span className="card-eyebrow">
                                    ICF Management
                                </span>
                                <span className="chip-live">Live Pilot</span>
                            </div>

                            <h2 className="card-title">
                                Every consent, defensible on inspection day.
                            </h2>

                            <p className="card-body">
                                Extract, execute, and explain informed consent
                                requirements on every participant — from
                                protocol amendment to BIMO-ready evidence
                                chain.
                            </p>

                            <div className="features">
                                <span className="feature">
                                    Amendment-aware re-consent
                                </span>
                                <span className="feature">
                                    Element-by-element 50.25 compliance
                                </span>
                                <span className="feature">
                                    BIMO-ready evidence
                                </span>
                            </div>

                            <div className="spacer" />

                            <div className="cta-row">
                                <Link
                                    to="/icf/story"
                                    className="btn-primary"
                                    data-testid="landing-icf-enter"
                                >
                                    Enter the pilot
                                    <span className="arrow">→</span>
                                </Link>
                                <a
                                    href="https://calendly.com/keshav-glifiq/glif-demo"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-secondary"
                                    data-testid="landing-icf-book"
                                >
                                    <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        aria-hidden="true"
                                    >
                                        <rect
                                            x="3.5"
                                            y="5"
                                            width="17"
                                            height="15"
                                            rx="2.5"
                                            stroke="currentColor"
                                            strokeWidth="1.8"
                                        />
                                        <path
                                            d="M3.5 9.5H20.5"
                                            stroke="currentColor"
                                            strokeWidth="1.8"
                                        />
                                        <path
                                            d="M8 3V7"
                                            stroke="currentColor"
                                            strokeWidth="1.8"
                                            strokeLinecap="round"
                                        />
                                        <path
                                            d="M16 3V7"
                                            stroke="currentColor"
                                            strokeWidth="1.8"
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                    Book a meeting
                                </a>
                            </div>
                        </div>
                    </section>

                    <div className="spacer" style={{ minHeight: 24 }} />

                    <footer>
                        Built for GRC, compliance, and audit teams · ©{" "}
                        {new Date().getFullYear()} AudRI
                    </footer>
                </main>
            </div>
        </>
    );
}
